import { Controller } from "@hotwired/stimulus";

// Renders a PDF with PDF.js: one box per page, each page drawn to a canvas
// only when it scrolls into view. Draws highlight boxes from coordinates
// normalised to 0..1 (from Document AI), keyed by page number. Also fills a
// strip of page thumbnails (links to #page-N, drawn lazily too) that marks
// the page being read and badges pages matching the search, and runs the
// find bar: previous/next step through every match in the document.
export default class extends Controller {
  static targets = ["pages", "thumbs", "findCount"];
  static values = { url: String, page: Number, highlights: Object, matchPages: Array, pdfjs: String, worker: String };

  async connect() {
    // Every match in reading order: each highlighted word, or just the page
    // where search matched another form of the word (so it has no box).
    this.matches = this.matchPagesValue.flatMap((n) => {
      const boxes = this.highlightsValue[n] || [];
      return boxes.length ? boxes.map((box, i) => ({ page: n, i, box })) : [{ page: n }];
    });
    this.matchIndex = -1;

    // Turbo restores cached pages with our old placeholders inside; start clean.
    this.pagesTarget.replaceChildren();
    if (this.hasThumbsTarget) this.thumbsTarget.replaceChildren();

    // PDF.js (from the pdfjs-dist package, copied into the app's assets by
    // `yarn build:pdfjs`) is loaded at runtime. The URL comes from a value
    // (not a literal) so esbuild leaves the import alone. No eval: the
    // Archive's Content-Security-Policy forbids it.
    const pdfjs = await import(this.pdfjsValue);
    pdfjs.GlobalWorkerOptions.workerSrc = this.workerValue;
    const pdf = await pdfjs.getDocument({ url: this.urlValue, isEvalSupported: false }).promise;

    // The visitor may have navigated away while the PDF was loading.
    if (!this.element.isConnected) return pdf.destroy();
    this.pdf = pdf;

    // Size every placeholder like page 1 so scrolling to a page lands correctly.
    const first = (await pdf.getPage(1)).getViewport({ scale: 1 });
    this.element.style.setProperty("--page-ratio", first.width / first.height);

    this.observer = new IntersectionObserver((entries) => this.renderVisible(entries), { rootMargin: "400px" });
    // The page crossing the middle of the window is the one being read.
    this.reading = new IntersectionObserver((entries) => this.trackReading(entries), { rootMargin: "-50% 0px -50% 0px" });
    if (this.hasThumbsTarget) {
      this.thumbObserver = new IntersectionObserver((entries) => this.renderVisible(entries, this.thumbObserver),
        { root: this.thumbsTarget, rootMargin: "200px" });
    }

    for (let n = 1; n <= pdf.numPages; n++) {
      this.pagesTarget.appendChild(this.placeholder(n));
      if (this.hasThumbsTarget) this.thumbsTarget.appendChild(this.thumb(n));
    }

    // Jump to the page asked for (?page=) or in the link (#page-N); otherwise stay at the top.
    const target = this.hasPageValue ? `page-${this.pageValue}` : location.hash.slice(1);
    if (target) document.getElementById(target)?.scrollIntoView();
  }

  disconnect() {
    this.observer?.disconnect();
    this.reading?.disconnect();
    this.thumbObserver?.disconnect();
    this.pdf?.destroy();
    this.pdf = null;
  }

  // An empty page box, rendered once it becomes visible.
  placeholder(n) {
    const div = document.createElement("div");
    div.className = "pdf-page";
    div.id = `page-${n}`;
    div.dataset.page = n;
    this.observer.observe(div);
    this.reading.observe(div);
    return div;
  }

  // A thumbnail link to page n, drawn once it scrolls into the strip.
  thumb(n) {
    const a = document.createElement("a");
    a.className = "item-thumb";
    a.href = `#page-${n}`;
    a.setAttribute("aria-label", `Page ${n}`);

    const div = document.createElement("div");
    div.className = "pdf-page item-thumb__page";
    div.dataset.page = n;
    const label = document.createElement("span");
    label.textContent = n;
    a.append(div, label);

    if (this.matchPagesValue.includes(n)) {
      const count = this.matches.filter((m) => m.page === n).length;
      a.classList.add("item-thumb--match");
      a.setAttribute("aria-label", `Page ${n}, ${count} ${count === 1 ? "match" : "matches"}`);
      const badge = document.createElement("span");
      badge.className = "item-thumb__badge";
      badge.textContent = count;
      a.appendChild(badge);
    }
    this.thumbObserver.observe(div);
    return a;
  }

  // Draws the boxes that have come into view. All are taken off the observer
  // (and marked) before drawing starts: drawing is slow, and a box still
  // observed could be reported again meanwhile and drawn twice.
  async renderVisible(entries, observer = this.observer) {
    const boxes = entries.filter((e) => e.isIntersecting).map((e) => e.target);
    boxes.forEach((box) => observer.unobserve(box));
    for (const box of boxes) {
      if (box.dataset.drawn) continue;
      box.dataset.drawn = "true";
      await this.renderPage(box, { highlights: observer === this.observer });
    }
  }

  // Draws page n to a canvas, then adds its highlights. The box takes the
  // page's own shape (until now it had page 1's); the canvas just covers it
  // (see _viewer.scss), so its size never changes the layout.
  async renderPage(div, { highlights = true } = {}) {
    if (!this.pdf) return; // disconnected; nothing to draw into
    const n = Number(div.dataset.page);
    const page = await this.pdf.getPage(n);
    const unscaled = page.getViewport({ scale: 1 });
    div.style.setProperty("--page-ratio", unscaled.width / unscaled.height);
    const scale = (div.clientWidth / unscaled.width) * window.devicePixelRatio;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    div.querySelector("canvas")?.remove();
    div.prepend(canvas);
    // Rendering is cancelled if the PDF is destroyed (we navigated away).
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise.catch(() => {});

    if (!highlights) return;
    (this.highlightsValue[n] || []).forEach(([x0, y0, x1, y1], i) => {
      const b = this.box(x0, y0, x1, y1);
      b.dataset.match = i;
      div.appendChild(b);
    });
    this.markCurrentMatch();
  }

  nextMatch() { this.goToMatch(this.matchIndex + 1); }

  previousMatch() { this.goToMatch(this.matchIndex - 1); }

  // Scrolls match +index+ (wrapping round) a third of the way down the
  // window, marks its box and updates the count.
  goToMatch(index) {
    if (!this.matches.length) return;
    this.matchIndex = (index + this.matches.length) % this.matches.length;
    const match = this.matches[this.matchIndex];
    const page = document.getElementById(`page-${match.page}`);
    if (!page) return; // PDF still loading

    const pageTop = page.getBoundingClientRect().top + window.scrollY;
    const top = match.box ? pageTop + match.box[1] * page.clientHeight - window.innerHeight / 3 : pageTop - 16;
    window.scrollTo({ top, behavior: "smooth" });

    this.findCountTarget.textContent = `Match ${this.matchIndex + 1} of ${this.matches.length} · page ${match.page}`;
    this.markCurrentMatch();
  }

  // Gives the current match's box (if drawn yet) a stronger style.
  markCurrentMatch() {
    for (const b of this.pagesTarget.querySelectorAll(".pdf-highlight--current")) b.classList.remove("pdf-highlight--current");
    const match = this.matches[this.matchIndex];
    if (!match?.box) return;
    this.pagesTarget.querySelector(`#page-${match.page} .pdf-highlight[data-match="${match.i}"]`)
      ?.classList.add("pdf-highlight--current");
  }

  // "Matching pages only": hides the other thumbnails.
  toggleMatchingOnly(event) {
    this.thumbsTarget.classList.toggle("item-thumbs--matching-only", event.target.checked);
  }

  // Marks the thumbnail of the page being read and keeps it in view in the
  // strip (scrolling only the strip, never the page).
  trackReading(entries) {
    if (!this.hasThumbsTarget) return;
    const entry = entries.find((e) => e.isIntersecting);
    if (!entry) return;

    const current = this.thumbsTarget.querySelector(`a[href="#${entry.target.id}"]`);
    for (const a of this.thumbsTarget.querySelectorAll("[aria-current]")) a.removeAttribute("aria-current");
    current.setAttribute("aria-current", "page");

    // The strip is position: relative, so offsets are within it.
    const strip = this.thumbsTarget;
    const top = current.offsetTop;
    const left = current.offsetLeft;
    if (top < strip.scrollTop || top + current.offsetHeight > strip.scrollTop + strip.clientHeight ||
        left < strip.scrollLeft || left + current.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      strip.scrollTo({ top: top - strip.clientHeight / 2 + current.offsetHeight / 2,
        left: left - strip.clientWidth / 2 + current.offsetWidth / 2, behavior: "smooth" });
    }
  }

  // A highlight box. Percentages make it scale with the page.
  box(x0, y0, x1, y1) {
    const b = document.createElement("div");
    b.className = "pdf-highlight";
    Object.assign(b.style, {
      left: `${x0 * 100}%`, top: `${y0 * 100}%`,
      width: `${(x1 - x0) * 100}%`, height: `${(y1 - y0) * 100}%`,
    });
    return b;
  }
}
